import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import './App.css';
import App from './App';
import ProtectedRoute from './components/ProtectedRoute';
import PermissionRoute from '@/components/PermissionRoute';
import GodOnlyRoute from '@/components/GodOnlyRoute';
import { PageLoader } from './components/PageLoader';
import ErrorPage from './components/ErrorPage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ROUTES } from './lib/routes';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from './providers/theme-provider';
import { useHealthStore } from '@/features/health/store/useHealthStore';
import { Auth0Provider } from '@auth0/auth0-react';
import Auth0TokenBridge from '@/components/Auth0TokenBridge';
// Netlify redirect to the new domain
// _redirects file and netlify.toml approaches did not work, so we are using this approach.
const NETLIFY_HOST = "radscheduler.netlify.app";
const PRIMARY_HOST = "schedule.transparentimaging.com";


// Lazy load page components
const HomePage = lazy(() => import('./features/home/pages/HomePage'));
const OutpatientPage = lazy(() => import('./features/outpatient/pages/OutpatientPage'));
const OutpatientEditPage = lazy(() => import('./features/outpatient/pages/OutpatientEditPage'));
const Analytics = lazy(() => import('./features/analytics/pages/Analytics'));
const Forecast = lazy(() => import('./features/forecast/pages/Forecast'));
const Health = lazy(() => import('./features/health/pages/Health'));
const EditSchedulePage = lazy(() => import('./features/edit/pages/EditSchedulePage'));
const SignIn = lazy(() => import('./features/signin/pages/SignIn'));
const Sites = lazy(() => import('./features/sites/pages/Sites'));
const Site = lazy(() => import('./features/sites/pages/Site'));
const Radiologists = lazy(() => import('./features/radiologists/pages/Radiologists'));
const Radiologist = lazy(() => import('./features/radiologists/pages/Radiologist'));
const UsersManagementPage = lazy(() => import('./features/users/pages/UsersManagementPage'));
const Roster = lazy(() => import('./features/roster/pages/Roster'));
const Specialties = lazy(() => import('./features/specialties/pages/Specialties'));
const StudiesPage = lazy(() => import('./features/studies/pages/StudiesPage'));
const StudyDetailPage = lazy(() => import('./features/studies/pages/StudyDetailPage'));
const AutomationTasksPage = lazy(() => import('./features/automation/pages/AutomationTasksPage'));
const TiPlatformTelemetry = lazy(() => import('./features/ti-platform-telemetry/pages/TiPlatformTelemetry'));
const IntakePage = lazy(() => import('./features/intake/pages/IntakePage'));
const OnboardingPage = lazy(() => import('./features/onboarding/pages/OnboardingPage'));
const VacationsPage = lazy(() => import('./features/vacations/pages/VacationsPage'));
const SupportSchedulePage = lazy(() => import('./features/support-schedule/pages/SupportSchedulePage'));
const RadSupportPeople = lazy(() => import('./features/rad-support/pages/RadSupportPeople'));
const RadSupportPerson = lazy(() => import('./features/rad-support/pages/RadSupportPerson'));

const queryClient = new QueryClient();

function requireNonEmptyAuth0Env(varName: string, value: unknown): string {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) {
    throw new Error(
      `Scheduler frontend: missing or empty Auth0 environment variable "${varName}". ` +
        `Set it in your environment or .env file before starting the app.`
    );
  }
  return trimmed;
}

const auth0Domain = requireNonEmptyAuth0Env(
  'VITE_AUTH0_DOMAIN',
  import.meta.env.VITE_AUTH0_DOMAIN
);
const auth0ClientId = requireNonEmptyAuth0Env(
  'VITE_AUTH0_CLIENT_ID',
  import.meta.env.VITE_AUTH0_CLIENT_ID
);
const rawAudience = import.meta.env.VITE_AUTH0_AUDIENCE;
const auth0Audience =
  typeof rawAudience === 'string' && rawAudience.trim() !== ''
    ? rawAudience.trim()
    : undefined;

const router = createBrowserRouter([
  {
    path: ROUTES.HOME,
    element: <ProtectedRoute />,
    // Fallback for failures in the auth/shell layer itself (ProtectedRoute).
    errorElement: <ErrorPage />,
    children: [
      {
        path: ROUTES.HOME,
        element: <App />,
        children: [
          {
            // Scoped boundary for page-level loader/render/action errors so a
            // single failing page shows ErrorPage inside App's Outlet while the
            // sidebar/shell stays intact, instead of replacing the whole app.
            errorElement: <ErrorPage />,
            children: [
          { 
            path: ROUTES.HOME, 
            element: (
              <PermissionRoute permission="view_schedule" redirectTo={ROUTES.SUPPORT_SCHEDULE}>
                <Suspense fallback={<PageLoader />}>
                  <HomePage />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.OUTPATIENT,
            element: (
              <PermissionRoute permission="view_schedule">
                <Suspense fallback={<PageLoader />}>
                  <OutpatientPage />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.OUTPATIENT_EDIT,
            element: (
              <PermissionRoute permission="edit_schedule">
                <Suspense fallback={<PageLoader />}>
                  <OutpatientEditPage />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.EDIT,
            element: (
              <PermissionRoute permission="edit_schedule">
                <Suspense fallback={<PageLoader />}>
                  <EditSchedulePage />
                </Suspense>
              </PermissionRoute>
            )
          },
          { 
            path: ROUTES.SITES, 
            element: (
              <PermissionRoute permission="manage_settings">
                <Suspense fallback={<PageLoader />}>
                  <Sites />
                </Suspense>
              </PermissionRoute>
            )
          },
          { 
            path: ROUTES.SITE_DETAIL, 
            element: (
              <PermissionRoute permission="manage_settings">
                <Suspense fallback={<PageLoader />}>
                  <Site />
                </Suspense>
              </PermissionRoute>
            )
          },
          { 
            path: ROUTES.RADIOLOGISTS, 
            element: (
              <PermissionRoute permission="manage_settings">
                <Suspense fallback={<PageLoader />}>
                  <Radiologists />
                </Suspense>
              </PermissionRoute>
            )
          },
          { 
            path: ROUTES.RADIOLOGIST_DETAIL, 
            element: (
              <PermissionRoute permission="manage_settings">
                <Suspense fallback={<PageLoader />}>
                  <Radiologist />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.SPECIALTIES,
            element: (
              <PermissionRoute permission="manage_settings">
                <Suspense fallback={<PageLoader />}>
                  <Specialties />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.STUDIES,
            element: (
              <PermissionRoute permission="manage_studies">
                <Suspense fallback={<PageLoader />}>
                  <StudiesPage />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.STUDY_DETAIL,
            element: (
              <PermissionRoute permission="manage_studies">
                <Suspense fallback={<PageLoader />}>
                  <StudyDetailPage />
                </Suspense>
              </PermissionRoute>
            )
          },
          { 
            path: ROUTES.HEALTH, 
            element: (
              <PermissionRoute permission="manage_settings">
                <Suspense fallback={<PageLoader />}>
                  <Health />
                </Suspense>
              </PermissionRoute>
            ),
            loader: () => useHealthStore.getState().list()
          },
          {
            path: ROUTES.AUTOMATION,
            element: (
              <GodOnlyRoute>
                <Suspense fallback={<PageLoader />}>
                  <AutomationTasksPage />
                </Suspense>
              </GodOnlyRoute>
            ),
          },
          {
            path: ROUTES.TI_TELEMETRY,
            element: (
              <GodOnlyRoute>
                <Suspense fallback={<PageLoader />}>
                  <TiPlatformTelemetry />
                </Suspense>
              </GodOnlyRoute>
            ),
          },
          { 
            path: ROUTES.USERS, 
            element: (
              <PermissionRoute permission="manage_users">
                <Suspense fallback={<PageLoader />}>
                  <UsersManagementPage />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.FORECAST,
            element: (
              <PermissionRoute permission="view_forecast">
                <Suspense fallback={<PageLoader />}>
                  <Forecast />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.ANALYTICS,
            element: (
              <PermissionRoute permission="view_analytics">
                <Suspense fallback={<PageLoader />}>
                  <Analytics />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.ROSTER,
            element: (
              <PermissionRoute permission="view_roster">
                <Suspense fallback={<PageLoader />}>
                  <Roster />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.INTAKE,
            element: (
              <PermissionRoute permission="access_intake">
                <Suspense fallback={<PageLoader />}>
                  <IntakePage />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.ONBOARDING,
            element: (
              <PermissionRoute permission="manage_onboarding_invites">
                <Suspense fallback={<PageLoader />}>
                  <OnboardingPage />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.VACATIONS,
            element: (
              <PermissionRoute permission="manage_vacations">
                <Suspense fallback={<PageLoader />}>
                  <VacationsPage />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.SUPPORT_SCHEDULE,
            element: (
              <PermissionRoute permission="view_support_schedule">
                <Suspense fallback={<PageLoader />}>
                  <SupportSchedulePage />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.RAD_SUPPORT,
            element: (
              <PermissionRoute permission="manage_rad_support">
                <Suspense fallback={<PageLoader />}>
                  <RadSupportPeople />
                </Suspense>
              </PermissionRoute>
            )
          },
          {
            path: ROUTES.RAD_SUPPORT_DETAIL,
            element: (
              <PermissionRoute permission="manage_rad_support">
                <Suspense fallback={<PageLoader />}>
                  <RadSupportPerson />
                </Suspense>
              </PermissionRoute>
            )
          }
            ],
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.SIGN_IN,
    element: (
      <Suspense fallback={<PageLoader />}>
        <SignIn />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  { path: ROUTES.NOT_FOUND, element: <Navigate to={ROUTES.HOME} replace /> },
]);


if (window.location.hostname === NETLIFY_HOST) {
  const target =
    window.location.protocol +
    "//" +
    PRIMARY_HOST +
    window.location.pathname +
    window.location.search +
    window.location.hash;

  window.location.replace(target);
} else {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <Auth0Provider
          domain={auth0Domain}
          clientId={auth0ClientId}
          cacheLocation="localstorage"
          useRefreshTokens
          authorizationParams={{
            redirect_uri: window.location.origin,
            ...(auth0Audience ? { audience: auth0Audience } : {}),
          }}
        >
          <Auth0TokenBridge />
          <ThemeProvider>
            <RouterProvider router={router} />
            <Toaster />
          </ThemeProvider>
        </Auth0Provider>
      </QueryClientProvider>
    </StrictMode>
  );
}
